import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TeamDropdown } from "../../../components/common/TeamDropdown";
import { SimpleDropdown } from "../../../components/common/SimpleDropdown";
import { fetchPlayerTeams, loadMorePlayerTeams } from "../../../features/team/teamSlice";

const locationOptions = [
  { value: "", label: "Select Location" },
  { value: "Home", label: "Home" },
  { value: "Neutral", label: "Neutral" },
];

export const AddGameForm = ({ onAddGame, loading }) => {
  const dispatch = useDispatch();
  const { teams, playerTeamsPagination, playerTeamsLoading } = useSelector((state) => state.team);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTermRef = useRef('');

  const [form, setForm] = useState({
    homeTeamId: "",
    awayTeamId: "",
    date: "",
    time: "",
    location: "",
    streamLink: "",
    status: "upcoming",
  });

  useEffect(() => {
    dispatch(fetchPlayerTeams({ page: 1, limit: 20 }));
  }, [dispatch]);

  // Handle search - called from TeamDropdown with debounce
  const handleSearch = useCallback((term) => {
    searchTermRef.current = term;
    dispatch(fetchPlayerTeams({ page: 1, limit: 20, teamName: term || undefined }));
  }, [dispatch]);

  // Handle load more - called when user scrolls to bottom
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !playerTeamsPagination?.hasMore) return;

    setLoadingMore(true);
    const nextPage = playerTeamsPagination.currentPage + 1;
    dispatch(loadMorePlayerTeams({
      page: nextPage,
      limit: 20,
      teamName: searchTermRef.current || undefined
    })).finally(() => {
      setLoadingMore(false);
    });
  }, [dispatch, loadingMore, playerTeamsPagination]);

  const handleChange = (field, value) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleHomeTeamSelect = (team) => {
    setForm((f) => ({ ...f, homeTeamId: team._id || team.id }));
  };

  const handleAwayTeamSelect = (team) => {
    setForm((f) => ({ ...f, awayTeamId: team._id || team.id }));
  };

  const handleSubmit = async () => {
    if (!form.homeTeamId || !form.awayTeamId || !form.date || !form.time) {
      alert('Please fill in all required fields');
      return;
    }

    if (form.homeTeamId === form.awayTeamId) {
      alert('Same teams cannot play match against each other');
      return;
    }

    await onAddGame({
      homeTeamId: form.homeTeamId,
      awayTeamId: form.awayTeamId,
      date: form.date,
      time: form.time,
      location: form.location || "Home",
      streamLink: form.streamLink || "",
      status: form.status,
    });

    // Reset form after successful submission
    setForm({
      homeTeamId: "",
      awayTeamId: "",
      date: "",
      time: "",
      location: "",
      streamLink: "",
      status: "upcoming",
    });
  };

  return (
    <div className="rounded-3xl text-left ">
      <span className="text-xs font-semibold text-jp-ink ">ADD GAMES</span>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-jp-ink text-left">
        {/* Row 1 */}
        <div className="flex flex-col gap-1">
          <span>Home Team <span className="text-red-600 dark:text-red-400">*</span></span>
          <div className="h-11">
            <TeamDropdown
              selectedTeam={form.homeTeamId}
              onSelectTeam={handleHomeTeamSelect}
              teams={teams || []}
              loading={playerTeamsLoading && teams.length === 0}
              placeholder="Select Home Team"
              variant="admin"
              onSearch={handleSearch}
              onLoadMore={handleLoadMore}
              hasMore={playerTeamsPagination?.hasMore || false}
              loadingMore={loadingMore}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span>Date <span className="text-red-600 dark:text-red-400">*</span></span>
          <input
            type="date"
            className="h-11 w-full rounded-lg px-3 bg-jp-card border border-jp-line text-xs text-jp-ink focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all [color-scheme:light] dark:[color-scheme:light] dark:[color-scheme:dark] cursor-pointer"
            value={form.date}
            onChange={(e) => handleChange("date", e.target.value)}
            required
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span>Location</span>
          <SimpleDropdown
            value={form.location}
            onChange={(val) => handleChange("location", val)}
            options={locationOptions}
            placeholder="Select Location"
            variant="admin"
            buttonClassName="h-11 w-full rounded-lg px-3 pr-8 bg-jp-card border border-jp-line text-xs text-jp-ink cursor-pointer flex items-center gap-2 whitespace-nowrap"
          />
        </div>

        {/* Row 2 */}
        <div className="flex flex-col gap-1">
          <span>Away Team <span className="text-red-600 dark:text-red-400">*</span></span>
          <div className="h-11">
            <TeamDropdown
              selectedTeam={form.awayTeamId}
              onSelectTeam={handleAwayTeamSelect}
              teams={teams || []}
              loading={playerTeamsLoading && teams.length === 0}
              placeholder="Select Away Team"
              variant="admin"
              onSearch={handleSearch}
              onLoadMore={handleLoadMore}
              hasMore={playerTeamsPagination?.hasMore || false}
              loadingMore={loadingMore}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span>Time <span className="text-red-600 dark:text-red-400">*</span></span>
          <input
            type="time"
            className="h-11 w-full rounded-lg px-3 bg-jp-card border border-jp-line text-xs text-jp-ink focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all [color-scheme:light] dark:[color-scheme:light] dark:[color-scheme:dark] cursor-pointer"
            value={form.time}
            onChange={(e) => handleChange("time", e.target.value)}
            required
          />
        </div>
        <div className="flex flex-col gap-1">
          <span>
            Stream Link{" "}
            <span className="text-[10px] text-jp-muted">
              (Add a link to the stream)
            </span>
          </span>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <input
                type="url"
                className="w-full h-11 rounded-lg pl-9 pr-3 bg-jp-card border border-jp-line text-xs text-jp-ink focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all placeholder:text-jp-muted"
                value={form.streamLink}
                onChange={(e) => handleChange("streamLink", e.target.value)}
                placeholder="https://stream.example.com"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-jp-muted">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="h-11 px-6 rounded-lg bg-sky-500 text-[11px] font-semibold text-white hover:bg-sky-400 whitespace-nowrap shadow disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  ADDING...
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  ADD GAME
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
